import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Banknote,
  Bell,
  Building2,
  CheckCircle2,
  CreditCard,
  Database,
  FileText,
  HandCoins,
  Inbox,
  Lock,
  Mail,
  RefreshCw,
  Settings,
  ShieldCheck,
  TrendingUp,
  Wallet,
  XCircle,
  Zap,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';

/**
 * "How billing works" reference page — internal documentation for platform staff.
 * Visualizes the customer billing loop, reseller commission/payout loop, dunning
 * cadence, and the webhook bridge between NestJS and Laravel POS.
 */
export function BillingFlow() {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1280px] mx-auto">
      <PageHeader
        eyebrow="Reference"
        title="How billing works"
        subtitle="End-to-end view of how a customer pays Possignin, how a reseller earns commission, and how the platform stays in sync with the POS."
      />

      {/* Quick-jump pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <JumpPill href="#paths" label="Payment paths" />
        <JumpPill href="#cadence" label="Email cadence" />
        <JumpPill href="#dunning" label="Dunning" />
        <JumpPill href="#lifecycle" label="Invoice lifecycle" />
        <JumpPill href="#commission" label="Reseller commission" />
        <JumpPill href="#webhooks" label="Webhook bridge" />
        <JumpPill href="#code" label="Where to find it" />
      </div>

      <Banner tone="info" className="mb-6" title="BD-market reality">
        Most Bangladesh customers do <strong>not</strong> save a card and do <strong>not</strong>{' '}
        use auto-payment. They prefer to <strong>click "Pay now" manually</strong> when each
        invoice email arrives, or <strong>pay cash at the office</strong>. Auto-charge is real but
        a minority path. The flow below is built to make manual + cash the default-good experience,
        not a fallback.
      </Banner>

      {/* ── Section: Three payment paths ──────────────────────────────── */}
      <section id="paths" className="mb-10">
        <SectionTitle eyebrow="Customer billing">Three payment paths</SectionTitle>

        <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
          <PathCard
            tone="brand"
            icon={<Inbox className="w-5 h-5" />}
            title="Path 1 · Manual click-to-pay"
            kicker="The default for most BD customers"
            badge="Most common"
            steps={[
              { icon: <FileText className="w-3.5 h-3.5" />, label: 'Cron creates invoice on next_invoice_at' },
              { icon: <Mail className="w-3.5 h-3.5" />, label: 'Email "Invoice ready" · big Pay-now button' },
              { icon: <CreditCard className="w-3.5 h-3.5" />, label: 'Customer clicks · SSLCommerz hosted checkout' },
              { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: 'IPN confirms · invoice paid · period extended' },
            ]}
            footer="No card saved. Customer clicks Pay each cycle. Pre-due reminders fire at T-2d and T-1d. SSLCommerz checkout supports Card / bKash / Nagad / bank in one screen."
          />

          <PathCard
            tone="emerald"
            icon={<HandCoins className="w-5 h-5" />}
            title="Path 2 · Cash to office"
            kicker="Pay in person · also very common"
            badge="Also common"
            steps={[
              { icon: <FileText className="w-3.5 h-3.5" />, label: 'Invoice issued (status: open)' },
              { icon: <HandCoins className="w-3.5 h-3.5" />, label: 'Customer drops cash at Sylhet office' },
              { icon: <Banknote className="w-3.5 h-3.5" />, label: 'Admin records via /admin/cash-entry' },
              { icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: 'Invoice → paid · receipt emailed · POS unsuspends' },
            ]}
            footer="Identical end-state as Path 1. provider='manual_cash' on the payment row. Reseller can also record this — see /admin/cash-entry."
          />

          <PathCard
            tone="amber"
            icon={<Zap className="w-5 h-5" />}
            title="Path 3 · Auto-charge"
            kicker="Saved method · minority of BD customers"
            badge="Optional"
            steps={[
              { icon: <FileText className="w-3.5 h-3.5" />, label: 'Cron creates invoice on next_invoice_at' },
              { icon: <CreditCard className="w-3.5 h-3.5" />, label: 'NestJS calls SSLCommerz with saved token' },
              { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: 'IPN confirms · invoice → paid' },
              { icon: <Mail className="w-3.5 h-3.5" />, label: 'One receipt email — no clicks required' },
            ]}
            footer="Customer must explicitly opt in by saving a card or setting up a bKash mandate from /subscription. Off by default."
          />
        </div>
      </section>

      {/* ── Section: No-saved-card timeline ─────────────────────────────── */}
      <section id="no-card" className="mb-10">
        <SectionTitle eyebrow="Path B in detail">What happens if the customer doesn't save a card</SectionTitle>

        <Card className="p-5 sm:p-7">
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-5">
            Most of our small-shop customers don't save a card — they prefer to click Pay each
            month. The platform never auto-charges them. Here's the exact sequence for a
            ৳500/mo Basic plan that renews on the 3rd:
          </p>

          <ol className="relative pl-8 space-y-5 border-l-2 border-zinc-200 dark:border-zinc-700">
            <Beat
              when="01 May (T−2d)"
              tone="info"
              icon={<FileText className="w-3 h-3" />}
              title="Invoice generated (DRAFT)"
              detail="NestJS billing-cron creates the invoice 2 days before due date so we have time to surface it. Status: draft, total ৳500."
            />
            <Beat
              when="01 May (T−2d) · 09:00"
              tone="info"
              icon={<Mail className="w-3 h-3" />}
              title="Email — Pre-bill notice"
              detail={`"Your next invoice arrives in 2 days." Includes the amount and a "Pay early" button.`}
            />
            <Beat
              when="03 May (T+0d) · 00:01"
              tone="brand"
              icon={<Inbox className="w-3 h-3" />}
              title="Invoice issued (OPEN) + email"
              detail={`Subject: "Invoice INV-2026-0345 is ready · ৳500 due today". Big "Pay now" button → SSLCommerz hosted checkout. PDF attached.`}
            />
            <Beat
              when="03 May · within minutes"
              tone="brand"
              icon={<CreditCard className="w-3 h-3" />}
              title="Customer clicks Pay → pays via SSLCommerz"
              detail="Card / bKash / Nagad / bank — whatever they pick. SSLCommerz captures, IPN fires, invoice → paid, receipt email sent. Period extends to 03 Jun."
            />

            <li className="text-xs uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mt-6">
              ↓ If they DON'T pay on the 3rd
            </li>

            <Beat
              when="04 May (T+1d) · 09:00"
              tone="info"
              icon={<Bell className="w-3 h-3" />}
              title="Email — Polite nudge"
              detail={`"Friendly reminder — your invoice is 1 day overdue." Subscription still active (3-day grace).`}
            />
            <Beat
              when="06 May (T+3d)"
              tone="warn"
              icon={<AlertTriangle className="w-3 h-3" />}
              title="Subscription → past_due"
              detail="State changes to past_due. POS still works (we don't suspend yet). Banner appears in their dashboard. Email: 'Your subscription is past due.'"
            />
            <Beat
              when="10 May (T+7d)"
              tone="warn"
              icon={<AlertTriangle className="w-3 h-3" />}
              title="Final notice email"
              detail={`"Final notice — POS suspends tomorrow." Includes Pay-now button + cash-to-office option.`}
            />
            <Beat
              when="11 May (T+8d)"
              tone="error"
              icon={<XCircle className="w-3 h-3" />}
              title="Subscription → suspended"
              detail={`POS at <slug>.itcitysoft.com starts showing the "Your POS is on hold" page. Webhook fires to Laravel: business.status_changed { suspended }. Email: "Your POS is on hold."`}
            />
            <Beat
              when="11 May–10 Jun"
              tone="error"
              icon={<Lock className="w-3 h-3" />}
              title="30-day suspended grace"
              detail="Customer can pay & restore at any point. Data is preserved. Reminder email at day 23 ('7 days until data archive')."
            />
            <Beat
              when="10 Jun (T+38d)"
              tone="critical"
              icon={<XCircle className="w-3 h-3" />}
              title="Subscription → canceled"
              detail="Account fully canceled. Data scheduled for deletion in 30 days (per data-retention policy)."
            />
          </ol>

          <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700 grid sm:grid-cols-3 gap-3 text-xs text-zinc-600 dark:text-zinc-300">
            <KeyPoint
              icon={<RefreshCw className="w-3.5 h-3.5" />}
              title="Pay any time — chain stops"
              detail="The customer can click Pay at any point in this 38-day sequence. The dunning chain stops immediately on success and the subscription returns to active."
            />
            <KeyPoint
              icon={<Banknote className="w-3.5 h-3.5" />}
              title="Cash works during suspension too"
              detail="Even after suspension, an admin recording cash via /admin/cash-entry restores POS access within seconds (via webhook to Laravel)."
            />
            <KeyPoint
              icon={<Mail className="w-3.5 h-3.5" />}
              title="Customer controls notification cadence"
              detail="They can opt out of pre-bill reminders in /profile, but legal-required emails (final notice, cancellation) always fire."
            />
          </div>
        </Card>
      </section>

      {/* ── Section: Email cadence ──────────────────────────────────────── */}
      <section id="cadence" className="mb-10">
        <SectionTitle eyebrow="Notifications">Email cadence at a glance</SectionTitle>
        <Card className="overflow-hidden">
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3 whitespace-nowrap">When</th>
                  <th className="text-left font-medium px-4 py-3">Subject line</th>
                  <th className="text-left font-medium px-4 py-3">Trigger</th>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Severity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                <EmailRow when="T−2d" subject={`"Your invoice arrives in 2 days"`} trigger="Cron · invoice generated" tone="info" />
                <EmailRow when="T+0d" subject={`"Invoice INV-… is ready · ৳X due today"`} trigger="Invoice issued · open status" tone="info" />
                <EmailRow when="T+0d (auto-charge)" subject={`"Receipt for INV-… · ৳X charged to •••8732"`} trigger="Auto-charge succeeded" tone="success" />
                <EmailRow when="T+0d (auto-fail)" subject={`"We couldn't charge your card"`} trigger="Auto-charge failed" tone="warn" />
                <EmailRow when="T+1d" subject={`"Friendly reminder — invoice 1 day overdue"`} trigger="Unpaid · subscription still active" tone="info" />
                <EmailRow when="T+3d" subject={`"Your subscription is past due"`} trigger="Subscription → past_due" tone="warn" />
                <EmailRow when="T+7d" subject={`"Final notice — POS suspends tomorrow"`} trigger="Last dunning attempt" tone="warn" />
                <EmailRow when="T+8d" subject={`"Your POS is on hold"`} trigger="Subscription → suspended" tone="error" />
                <EmailRow when="T+30d" subject={`"7 days until data archive"`} trigger="30-day suspended cliff" tone="error" />
                <EmailRow when="T+38d" subject={`"Your account has been canceled"`} trigger="Subscription → canceled" tone="error" />
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* ── Section: Invoice lifecycle ──────────────────────────────────── */}
      <section id="lifecycle" className="mb-10">
        <SectionTitle eyebrow="Data model">Invoice & subscription lifecycle</SectionTitle>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 sm:p-6">
            <CardHeader title="Invoice states" subtitle="invoices.status enum (NestJS schema §4.8)" />
            <div className="space-y-2 text-sm">
              <Lifecycle from="draft" arrow="→" to="open" tone="info" detail="Auto on issue date" />
              <Lifecycle from="open" arrow="→" to="paid" tone="success" detail="On successful payment IPN" />
              <Lifecycle from="open" arrow="→" to="uncollectible" tone="error" detail="After 38d unpaid" />
              <Lifecycle from="open" arrow="→" to="void" tone="neutral" detail="Admin manually voids (rare)" />
              <Lifecycle from="paid" arrow="→" to="refunded" tone="warn" detail="Full refund issued" />
              <Lifecycle from="paid" arrow="→" to="partial_refunded" tone="warn" detail="Partial refund issued" />
            </div>
          </Card>

          <Card className="p-5 sm:p-6">
            <CardHeader title="Subscription states" subtitle="subscriptions.status enum" />
            <div className="space-y-2 text-sm">
              <Lifecycle from="trial" arrow="→" to="active" tone="success" detail="Trial paid (or expired & charged)" />
              <Lifecycle from="active" arrow="→" to="past_due" tone="warn" detail="3 days unpaid" />
              <Lifecycle from="past_due" arrow="→" to="suspended" tone="error" detail="8 days unpaid · POS blocked" />
              <Lifecycle from="suspended" arrow="→" to="active" tone="success" detail="Pay & restore" />
              <Lifecycle from="suspended" arrow="→" to="canceled" tone="critical" detail="38 days unpaid" />
              <Lifecycle from="active" arrow="→" to="canceled" tone="critical" detail="Voluntary cancel + period end" />
            </div>
          </Card>
        </div>
      </section>

      {/* ── Section: Reseller commission ─────────────────────────────────── */}
      <section id="commission" className="mb-10">
        <SectionTitle eyebrow="The other loop">Reseller commission + payout</SectionTitle>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 sm:p-7">
            <CardHeader
              title="Per-payment accrual"
              subtitle="Inside the SSLCommerz IPN handler"
            />
            <ol className="space-y-3 text-sm">
              <NumberedStep n={1} icon={<CheckCircle2 className="w-3.5 h-3.5" />}>
                Customer payment confirmed via IPN
              </NumberedStep>
              <NumberedStep n={2} icon={<TrendingUp className="w-3.5 h-3.5" />}>
                If <span className="mono">businesses.reseller_id</span> is set, compute split:
                <br />
                <span className="mono text-xs text-zinc-600 dark:text-zinc-300">
                  commission = amount × reseller.commission_pct / 100
                </span>
              </NumberedStep>
              <NumberedStep n={3} icon={<Wallet className="w-3.5 h-3.5" />}>
                Insert into <span className="mono">reseller_payout_lines</span>:{' '}
                <span className="mono text-xs">{'{ reseller_id, payment_id, business_id, gross, commission, accrued_at }'}</span>
              </NumberedStep>
              <NumberedStep n={4} icon={<Lock className="w-3.5 h-3.5" />}>
                Line sits in <strong>pending</strong> until next payout cycle
              </NumberedStep>
            </ol>
          </Card>

          <Card className="p-5 sm:p-7">
            <CardHeader title="Manual payout cycle" subtitle="Every 15 or 30 days · platform admin transfers offline" />
            <ol className="space-y-3 text-sm">
              <NumberedStep n={1} icon={<Settings className="w-3.5 h-3.5" />}>
                Reseller fills payout destination in{' '}
                <Link to="/reseller/settings" className="underline mono">
                  /reseller/settings
                </Link>
                : either a <strong>bKash number</strong> or a <strong>bank account</strong> (name,
                number, routing, branch).
              </NumberedStep>
              <NumberedStep n={2} icon={<RefreshCw className="w-3.5 h-3.5" />}>
                Platform admin opens{' '}
                <Link to="/admin/resellers/$id" params={{ id: 'rsl_1' }} className="underline mono">
                  /admin/resellers/&lt;id&gt;
                </Link>{' '}
                → <strong>Payouts</strong> tab on each <strong>15th or 30th</strong> (configurable
                per reseller). Sees the running due balance.
              </NumberedStep>
              <NumberedStep n={3} icon={<HandCoins className="w-3.5 h-3.5" />}>
                Admin <strong>transfers funds offline</strong> using the reseller's saved bKash /
                bank info. <em>The platform never touches a B2B disbursement API.</em>
              </NumberedStep>
              <NumberedStep n={4} icon={<FileText className="w-3.5 h-3.5" />}>
                Admin clicks <strong>"Record manual transfer"</strong> in the same UI to log the
                payout: amount, period, transaction reference (bKash trx ID or bank reference), date,
                note. Marks lines as <span className="mono">paid</span>.
              </NumberedStep>
              <NumberedStep n={5} icon={<Mail className="w-3.5 h-3.5" />}>
                Reseller receives a payout receipt email with the full breakdown.
              </NumberedStep>
            </ol>
          </Card>
        </div>

        <Banner tone="info" className="mt-4" title="Why manual?">
          BD market reality: B2B disbursement APIs (bKash B2B, Nagad B2B, instant bank rails) require
          paid integrations and merchant agreements that don't make sense at our reseller volumes.
          A manual transfer + manual recording on the 15th / 30th of each month is faster to operate
          and gives finance an explicit checkpoint to review fraud / chargebacks before paying out.
        </Banner>

        <Banner tone="warning" className="mt-4">
          <strong>Suspended reseller exception:</strong> Lines keep accruing but the admin doesn't
          transfer until reactivation. On termination for fraud: held lines forfeit per the partner
          agreement. See the suspend-reseller modal on{' '}
          <Link to="/admin/resellers/$id" params={{ id: 'rsl_1' }} className="underline font-medium">
            any reseller's detail page
          </Link>{' '}
          for the full impact list.
        </Banner>
      </section>

      {/* ── Section: Webhook bridge ─────────────────────────────────────── */}
      <section id="webhooks" className="mb-10">
        <SectionTitle eyebrow="System integration">Webhook bridge — NestJS ↔ Laravel ↔ SSLCommerz</SectionTitle>

        <Card className="p-5 sm:p-8">
          <pre className="mono text-[11px] leading-relaxed text-zinc-700 dark:text-zinc-200 overflow-x-auto whitespace-pre">
{`           ┌──────────────────────┐
           │     SSLCommerz       │  hosted checkout · captures payment
           └──────────┬───────────┘
                      │ HMAC-signed IPN
                      │ POST /webhooks/sslcommerz/ipn
                      ▼
           ┌──────────────────────┐
           │    NestJS (SaaS)     │  · verify signature
           │                      │  · idempotency check (event_id)
           │                      │  · update payments + invoice + subscription
           │                      │  · compute reseller commission
           │                      │  · enqueue receipt email (BullMQ)
           └──────────┬───────────┘
                      │ HMAC-signed webhook
                      │ POST /webhooks/possignin/business-status
                      ▼
           ┌──────────────────────┐
           │  Laravel POS (Ops)   │  · verify signature
           │                      │  · upsert Redis: business_status:{id}
           │                      │  · POS middleware reads this on every request
           └──────────────────────┘`}
          </pre>

          <div className="mt-5 grid sm:grid-cols-3 gap-3 text-xs">
            <KeyPoint
              icon={<ShieldCheck className="w-3.5 h-3.5" />}
              title="HMAC + idempotency keys"
              detail="Both directions sign payloads with a shared secret. event_id deduplicates retries (SSLCommerz can fire the same IPN multiple times)."
            />
            <KeyPoint
              icon={<Database className="w-3.5 h-3.5" />}
              title="Redis is the hot path"
              detail="Laravel POS doesn't query NestJS on each request. It reads business_status from Redis, falls back to NestJS only on cache miss (TTL 5 min)."
            />
            <KeyPoint
              icon={<RefreshCw className="w-3.5 h-3.5" />}
              title="Retries with exponential backoff"
              detail="If Laravel is down, NestJS retries the webhook up to 8 times over 24h. The audit log shows every attempt."
            />
          </div>
        </Card>
      </section>

      {/* ── Section: Where in the codebase ──────────────────────────────── */}
      <section id="code" className="mb-10">
        <SectionTitle eyebrow="Dev reference">Where each piece lives in the code</SectionTitle>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Concern</th>
                  <th className="text-left font-medium px-4 py-3">Code path (planned NestJS)</th>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Currently visible in mock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                <CodeRow concern="Invoice generation cron" path="apps/api/src/billing/jobs/issue-invoice.job.ts" link="/admin/audit" linkLabel="Audit · invoice.create" />
                <CodeRow concern="SSLCommerz init endpoint" path="apps/api/src/payments/sslcommerz/sslcommerz.controller.ts" link="/billing/inv-1" linkLabel="Pay button on invoice detail" />
                <CodeRow concern="SSLCommerz IPN handler" path="apps/api/src/webhooks/sslcommerz-ipn.controller.ts" link="/admin/audit" linkLabel="Audit · payment.received" />
                <CodeRow concern="Auto-charge cron" path="apps/api/src/billing/jobs/auto-charge.job.ts" link="/admin/audit" linkLabel="Audit · payment.failed cases" />
                <CodeRow concern="Dunning state machine" path="apps/api/src/billing/dunning.service.ts" link="/admin/audit" linkLabel="Audit · subscription.past_due" />
                <CodeRow concern="Manual cash entry" path="apps/api/src/payments/manual-cash.controller.ts" link="/admin/cash-entry" linkLabel="/admin/cash-entry" />
                <CodeRow concern="Commission accrual" path="apps/api/src/payouts/accrual.service.ts" link="/admin/resellers/$id" linkLabel="Reseller · Payments tab" />
                <CodeRow concern="Reseller payout cron" path="apps/api/src/payouts/jobs/monthly-payout.job.ts" link="/admin/resellers/$id" linkLabel="Reseller · Payouts tab" />
                <CodeRow concern="Webhook → Laravel POS" path="apps/api/src/webhooks/dispatch.service.ts" link="/admin/webhooks" linkLabel="/admin/webhooks" />
                <CodeRow concern="Customer-facing receipt email" path="apps/api/src/notifications/templates/receipt.tsx" link="/billing/inv-1" linkLabel="View invoice" />
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <div className="mt-8 flex flex-wrap gap-2">
        <Link to="/admin/audit">
          <Button variant="secondary" size="sm">View live audit log</Button>
        </Link>
        <Link to="/admin/cash-entry">
          <Button variant="secondary" size="sm">Record cash payment</Button>
        </Link>
        <Link to="/admin/webhooks">
          <Button variant="secondary" size="sm">Webhook deliveries</Button>
        </Link>
        <Link to="/admin/gateways">
          <Button variant="secondary" size="sm">Configure SSLCommerz</Button>
        </Link>
      </div>
    </div>
  );
}

/* ── Small components ──────────────────────────────────────────────────── */

function SectionTitle({ children, eyebrow }: { children: React.ReactNode; eyebrow: string }) {
  return (
    <div className="mb-4">
      <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">
        {eyebrow}
      </p>
      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
        {children}
      </h2>
    </div>
  );
}

function JumpPill({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
    >
      {label}
    </a>
  );
}

function PathCard({
  tone,
  icon,
  title,
  kicker,
  badge,
  steps,
  footer,
}: {
  tone: 'brand' | 'amber' | 'emerald';
  icon: React.ReactNode;
  title: string;
  kicker: string;
  badge?: string;
  steps: Array<{ icon: React.ReactNode; label: string }>;
  footer: string;
}) {
  const palette = {
    brand: 'text-brand dark:text-brand-dm bg-brand-subtle dark:bg-brand-dm-subtle/40',
    amber: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40',
    emerald: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40',
  }[tone];
  const badgePalette = {
    brand: 'bg-brand text-white dark:bg-brand-dm dark:text-zinc-950',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
    emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
  }[tone];
  return (
    <Card className="p-5 sm:p-6 flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className={cn('w-10 h-10 grid place-items-center rounded-lg', palette)}>{icon}</div>
        {badge && (
          <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider', badgePalette)}>
            {badge}
          </span>
        )}
      </div>
      <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">{title}</h3>
      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{kicker}</p>
      <ol className="mt-4 space-y-2 flex-1">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200">
            <span className="w-5 h-5 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0 mt-0.5">
              {s.icon}
            </span>
            <span className="flex-1">{s.label}</span>
          </li>
        ))}
      </ol>
      <p className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
        {footer}
      </p>
    </Card>
  );
}

function Beat({
  when,
  tone,
  icon,
  title,
  detail,
}: {
  when: string;
  tone: 'info' | 'brand' | 'warn' | 'error' | 'critical';
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  const dot = {
    info: 'bg-zinc-400 dark:bg-zinc-500',
    brand: 'bg-brand dark:bg-brand-dm',
    warn: 'bg-amber-500',
    error: 'bg-rose-500',
    critical: 'bg-rose-700',
  }[tone];
  return (
    <li className="relative">
      <span className={cn('absolute -left-[34px] top-1.5 w-3.5 h-3.5 rounded-full ring-4 ring-zinc-50 dark:ring-zinc-900', dot)}>
        <span className="absolute inset-0 grid place-items-center text-white text-[8px]">{icon}</span>
      </span>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 tabular-nums">
        {when}
      </div>
      <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 mt-0.5">{title}</div>
      <p className="text-xs text-zinc-600 dark:text-zinc-300 mt-1 max-w-2xl leading-relaxed">{detail}</p>
    </li>
  );
}

function KeyPoint({ icon, title, detail }: { icon: React.ReactNode; title: string; detail: string }) {
  return (
    <div className="rounded-md border border-zinc-200 dark:border-zinc-700 p-3">
      <div className="flex items-center gap-1.5 font-medium text-zinc-900 dark:text-zinc-100 mb-1">
        <span className="text-zinc-500 dark:text-zinc-400">{icon}</span>
        {title}
      </div>
      <p className="text-zinc-600 dark:text-zinc-300 leading-relaxed">{detail}</p>
    </div>
  );
}

function EmailRow({
  when,
  subject,
  trigger,
  tone,
}: {
  when: string;
  subject: string;
  trigger: string;
  tone: 'info' | 'success' | 'warn' | 'error';
}) {
  const dot = {
    info: 'bg-sky-500',
    success: 'bg-emerald-500',
    warn: 'bg-amber-500',
    error: 'bg-rose-500',
  }[tone];
  const label = { info: 'Info', success: 'Receipt', warn: 'Warning', error: 'Critical' }[tone];
  return (
    <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
      <td className="px-5 sm:px-6 py-3 text-zinc-700 dark:text-zinc-200 mono tabular-nums whitespace-nowrap">{when}</td>
      <td className="px-4 py-3 text-zinc-900 dark:text-zinc-100">{subject}</td>
      <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">{trigger}</td>
      <td className="px-5 sm:px-6 py-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-700 dark:text-zinc-200">
          <span className={cn('w-1.5 h-1.5 rounded-full', dot)} />
          {label}
        </span>
      </td>
    </tr>
  );
}

function Lifecycle({
  from,
  arrow,
  to,
  tone,
  detail,
}: {
  from: string;
  arrow: string;
  to: string;
  tone: 'success' | 'info' | 'warn' | 'error' | 'critical' | 'neutral';
  detail: string;
}) {
  const toneTone = (tone === 'critical' ? 'error' : tone) as 'success' | 'info' | 'warning' | 'error' | 'neutral';
  return (
    <div className="flex items-center gap-3 py-1">
      <Badge tone="neutral" dot={false}>
        <span className="mono text-[11px]">{from}</span>
      </Badge>
      <ArrowRight className="w-3.5 h-3.5 text-zinc-400" />
      <Badge tone={toneTone === 'warning' ? 'warning' : toneTone} dot={false}>
        <span className="mono text-[11px]">{to}</span>
      </Badge>
      <span className="text-xs text-zinc-600 dark:text-zinc-300 ml-auto truncate">{detail}</span>
    </div>
  );
}

function NumberedStep({ n, icon, children }: { n: number; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-6 h-6 grid place-items-center rounded-full bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 text-[11px] font-semibold flex-shrink-0">
        {n}
      </span>
      <div className="flex-1 min-w-0 leading-relaxed text-zinc-700 dark:text-zinc-200">
        <span className="inline-flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400 mr-1.5">{icon}</span>
        {children}
      </div>
    </li>
  );
}

function CodeRow({
  concern,
  path,
  link,
  linkLabel,
}: {
  concern: string;
  path: string;
  link: string;
  linkLabel: string;
}) {
  return (
    <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
      <td className="px-5 sm:px-6 py-3 text-zinc-900 dark:text-zinc-100 font-medium">{concern}</td>
      <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 mono text-xs">{path}</td>
      <td className="px-5 sm:px-6 py-3">
        <Link to={link as never} className="text-brand dark:text-brand-dm hover:underline mono text-xs">
          {linkLabel}
        </Link>
      </td>
    </tr>
  );
}
